import {
  Component,
  HostListener,
  inject,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { PageTitleService } from '../../core/services/page-title.service';
import {
  filter,
  Observable,
  Subject,
  takeUntil,
  combineLatest,
  map,
  distinctUntilChanged,
  debounceTime,
} from 'rxjs';
import { DrawerService } from '../../core/services/drawer.service';
import { OrganizationFormComponent } from '../../features/organizations/organization-form/organization-form.component';
import { SvgIconComponent } from 'angular-svg-icon';
import { DRAWER_IDS } from '../../core/constants/drawer-ids.constant';
import { ImportDataComponent } from '../../features/organizations/import-data/import-data.component';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { CreatePreBipCaseComponent } from '../../features/insights/pre-bip/create-pre-bip-case/create-pre-bip-case.component';
import { CreateBipCaseComponent } from '../../features/insights/behavior-intervention-plan/create-bip-case/create-bip-case.component';
import { TAB_NAME } from '../../core/constants/tab.constant';
import { TemplateLogsComponent } from '../../features/bip-template/template-logs/template-logs.component';
import { LayoutService } from '../../core/services/layout.service';
import { NewStrategyComponent } from '../../features/administration/strategies/new-strategy/new-strategy.component';
import { DrawerHeaderComponent } from '../drawer-header/drawer-header.component';
import { BipTemplateFormComponent } from '../../features/bip-template/bip-template-form/bip-template-form.component';
import { TemplateMode } from '../../features/bip-template/bip-template.enums';
import { OrganizationDetailModel } from '../../features/organizations/organization.interface';
import { AdminStrategyService } from '../../features/administration/strategies/admin-strategy.service';
import { EditUserComponent } from '../../features/administration/user-management/edit-user/edit-user.component';
import { NavigationEnd, Router } from '@angular/router';
import { NotificationsSettingsComponent } from '../../features/notifications/notifications-settings/notifications-settings.component';
import { BroadcastFormComponent } from '../../features/broadcast-config/broadcast-form/broadcast-form.component';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { DashboardCommonComponent } from '../../features/activity-dashboard/dashboard-common';
import {
  EnvironmentService,
  EnvironmentInfo,
} from '../../core/services/environment.service';
import { OrganizationSearchComponent } from '../organization-search/organization-search.component';
import { NotificationService } from '../../core/services/notification.service';
import { OrganizationSelectionService } from '../../core/services/organization-selection.service';
import { LookupItem } from '../../core/models/lookup.models';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { UserRole } from '../../features/activity-dashboard/activity-dashboard.enums';
import { CmsRoleAccessService } from '../../core/services/cms-role-access.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    NzPageHeaderModule,
    CommonModule,
    NzButtonComponent,
    NzBreadCrumbModule,
    SvgIconComponent,
    NzDropDownModule,
    DrawerHeaderComponent,
    NzDatePickerModule,
    NzFormModule,
    FormsModule,
    ReactiveFormsModule,
    NzSelectModule,
    OrganizationSearchComponent,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent
  extends DashboardCommonComponent
  implements OnInit, OnChanges, OnDestroy
{
  private readonly adminStrategyService = inject(AdminStrategyService);
  private readonly router = inject(Router);
  private readonly environmentService = inject(EnvironmentService);
  private readonly cmsRoleAccessService = inject(CmsRoleAccessService);

  @ViewChild('importFooterTpl') importFooterTpl!: TemplateRef<void>;
  @ViewChild('drawerTemplateLog') drawerTemplateLog!: TemplateRef<void>;
  @ViewChild('drawerAddTemplate') drawerAddTemplate!: TemplateRef<void>;
  @ViewChild('drawerNewStrategy') drawerNewStrategy!: TemplateRef<void>;
  @ViewChild('drawerAddUser') drawerAddUser!: TemplateRef<void>;
  @ViewChild('newOrganizationFooterTpl')
  newOrganizationFooterTpl!: TemplateRef<void>;

  tabName = TAB_NAME;

  title$: Observable<string>;
  isExporting$ = this.adminStrategyService.isExporting$;
  isInsightAdminRoute$ = this.commonService.isInsightAdminRoute$;
  isOrgRoute$ = this.commonService.isOrgRoute$;
  isUserManageRoute$ = this.commonService.isUserManageRoute$;
  isBipTemplateRoute$ = this.commonService.isBipTemplateRoute$;
  isNewStrategyRoute$ = this.commonService.isNewStrategyRoute$;
  isUserSearchRoute$ = this.commonService.isUserSearchRoute$;
  isNotificationRoute$ = this.commonService.isNotificationRoute$;
  isBroadcastRoute$ = this.commonService.isBroadcastRoute$;
  isDashboardRoute$ = this.commonService.isDashboardRoute$;
  isHomeRoute$ = this.commonService.isHomeRoute$;

  showMobileCta$ = combineLatest([
    this.isOrgRoute$,
    this.isInsightAdminRoute$,
    this.isUserManageRoute$,
    this.isBipTemplateRoute$,
    this.isNewStrategyRoute$,
    this.isNotificationRoute$,
    this.isBroadcastRoute$,
  ]).pipe(
    map(
      ([
        isOrg,
        isInsightAdmin,
        isUserManage,
        isBipTemplate,
        isNewStrategy,
        isNotification,
        isBroadcast,
      ]) =>
        isOrg ||
        isInsightAdmin ||
        isUserManage ||
        isBipTemplate ||
        isNewStrategy ||
        (isNotification && this.isPrefSettingsAdmin) ||
        isBroadcast
    )
  );

  showOrganizationSearch = false;

  activeTab: string = TAB_NAME.PRE_BIP;
  isScrolled = false;
  currentRoute = '';
  isMobile = false;
  organizationId = 0;
  override orgData: OrganizationDetailModel | null = null;
  newOrganizationDrawerVisible = false;
  importDrawerVisible = false;
  isHamburgerVisible = false;
  isPrefSettingsAdmin = false;
  isRangePickerOpen = false;
  showNotificationSettings = false;
  isMyOnlyViewControl = new FormControl<boolean>(true, { nonNullable: true });
  isViewTypeAllowed = false;
  readonly dashboardViewOptions = [
    { label: 'My Dashboard', value: true },
    { label: 'District Dashboard', value: false },
  ];
  dateRange: Date[] = [
    new Date(new Date().setDate(new Date().getDate() - 364)),
    new Date(),
  ];
  environmentInfo: EnvironmentInfo = { isActive: false, label: '' };
  dateRangeError = '';
  private readonly MAX_DAYS = 365;
  isManageSettignRoute = false;

  private readonly HAMBURGER_VISIBLE_ROUTES = ['/learners', '/lms', '/content'];
  private readonly MANAGE_SETTINGS_ROUTES = ['/manage-settings'];
  private readonly headerDestroy$ = new Subject<void>();
  selectedOrganizationId: number | null = 0;
  private readonly searchSubject = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  orgList: LookupItem[] = [];

  constructor(
    private readonly pageTitleService: PageTitleService,
    private readonly drawerService: DrawerService,
    private readonly layoutService: LayoutService,
    private readonly notificationService: NotificationService,
    private readonly orgSelection: OrganizationSelectionService
  ) {
    super();
    this.activityDashboardService.updateDateRange(this.dateRange);
    this.title$ = this.pageTitleService.title$;
    this.initSubscriptions();
    this.setupResizeListener();
    this.environmentInfo = this.environmentService.environmentInfo;
    this.orgSelection.setIsMyOnlyView(this.isMyOnlyViewControl.getRawValue());

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.headerDestroy$)
      )
      .subscribe(() => {
        this.checkHamburgerVisibility();
        this.updateShowOrganizationSearch();
      });

    this.searchSubject
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(searchTerm => {
        this.performSearch(searchTerm);
      });

    this.isMyOnlyViewControl.valueChanges
      .pipe(takeUntil(this.headerDestroy$))
      .subscribe(isMyOnlyView => {
        this.orgSelection.setIsMyOnlyView(isMyOnlyView);
      });
  }

  private performSearch(value: string) {
    if (this.userData?.isSuperAdmin) {
      this.commonService
        .getDataByType('Organization', value)
        .pipe(takeUntil(this.destroy$))
        .subscribe(orgList => {
          this.orgList = orgList.result;
        });
    }
  }

  ngOnInit(): void {
    this.commonService.activeTab$.subscribe(tabName => {
      if (tabName) {
        this.activeTab = tabName;
        this.cdr.detectChanges();
      }
    });

    this.orgSelection.orgId$
      .pipe(takeUntil(this.headerDestroy$))
      .subscribe(orgId => {
        if (orgId !== null && orgId !== this.selectedOrg) {
          this.selectedOrg = orgId;
        } else if (orgId === null) {
          this.selectedOrg = 0;
        }
      });

    this.checkHamburgerVisibility();
    this.updateShowOrganizationSearch();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeTab']) {
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.checkScreenSize.bind(this));
    this.headerDestroy$.next();
    this.headerDestroy$.complete();
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    this.isScrolled = window.scrollY > 0;
  }

  toggleSidebar(event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    const currentState = this.layoutService.currentSidebarState;
    this.layoutService.setSidebarState(!currentState);

    if (currentState) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    this.cdr.detectChanges();
  }

  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openImportDrawer();
    }
  }

  openNewOrganizationDrawer(): void {
    this.drawerService.openDrawer(
      OrganizationFormComponent,
      'Add New Organization',
      520,
      true,
      undefined,
      this.newOrganizationFooterTpl as unknown as TemplateRef<object>,
      DRAWER_IDS.ORGANIZATION
    );
  }

  openImportDrawer(): void {
    this.drawerService.openDrawer(
      ImportDataComponent,
      'Import Data File',
      520,
      true,
      undefined,
      undefined,
      DRAWER_IDS.IMPORT
    );
  }

  openStrategyDrawer(): void {
    this.drawerService.openDrawer(
      NewStrategyComponent,
      'New Strategy',
      1170,
      true
    );
  }

  openNewStrategyDrawer(): void {
    this.drawerService.openDrawer(
      NewStrategyComponent,
      this.drawerNewStrategy as unknown as TemplateRef<object>,
      780,
      true,
      undefined,
      {
        tableParams: {
          pageSize: 10,
          pageIndex: 1,
          sortField: null,
          sortOrder: undefined,
          searchTerm: '',
          ageLevels: [],
          environments: [],
          antecedents: [],
          functions: [],
          languageLevel: [],
        },
      },
      DRAWER_IDS.CREATE_ADMIN_STRATEGY
    );
  }

  exportStrategyData(): void {
    this.adminStrategyService.exportStrategyData();
  }

  openAddUserComponentDrawer(): void {
    this.drawerService.openDrawer(
      EditUserComponent,
      this.drawerAddUser as unknown as TemplateRef<object>,
      870,
      true,
      undefined,
      {
        orgData: this.selectedOrg,
        roleList: [],
        mode: 'CREATE',
        pageIndex: 1,
      },
      DRAWER_IDS.MANAGE_EDIT_USER
    );
  }

  checkRoleFornewCase(): boolean {
    return this.cmsRoleAccessService.computeAllowFromCmsRole(
      this.userRoles,
      [UserRole.CMSManager, UserRole.CMSDataEntry, UserRole.CMSBrowser],
      this.userData?.isSuperAdmin ?? false
    );
  }

  openNewBipCaseDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.BIP);
    this.drawerService.openDrawer(
      CreateBipCaseComponent,
      'New Case',
      780,
      true,
      undefined,
      undefined,
      DRAWER_IDS.BIP
    );
  }

  openNewPreBipCaseDrawer(): void {
    this.drawerService.openDrawer(
      CreatePreBipCaseComponent,
      'New Incident Tracker',
      780,
      true,
      undefined,
      undefined,
      DRAWER_IDS.PREBIP
    );
  }

  openNotificationsSettingsDrawer(): void {
    this.drawerService.openDrawer(
      NotificationsSettingsComponent,
      'Notifications Settings',
      1170,
      true,
      undefined,
      { selectedOrganizationId: this.selectedOrganizationId },
      DRAWER_IDS.NOTIFICATION_SETTINGS
    );
  }

  openBroadcastDrawer(): void {
    this.drawerService.openDrawer(
      BroadcastFormComponent,
      'Add Broadcast',
      520,
      true,
      undefined,
      {
        mode: 'CREATE',
        id: null,
      },
      DRAWER_IDS.BROADCAST
    );
  }

  openTemplateLogsDrawer(): void {
    this.drawerService.openDrawer(
      TemplateLogsComponent,
      this.drawerTemplateLog as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {},
      DRAWER_IDS.TEMPLATE_LOGS
    );
  }

  openAddTemplateDrawer(): void {
    this.drawerService.openDrawer(
      BipTemplateFormComponent,
      this.drawerAddTemplate as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        mode: TemplateMode.CREATE,
        organizationId: this.selectedOrg,
        selectedTemplate: null,
      },
      DRAWER_IDS.ADD_TEMPLATE_V2
    );
  }

  closeTemplateLogDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.TEMPLATE_LOGS);
  }

  closeAddTemplateDrawer(): void {
    // this.drawerService.closeDrawer(DRAWER_IDS.ADD_TEMPLATE);
    this.drawerService.closeDrawer(DRAWER_IDS.ADD_TEMPLATE_V2);
  }

  private initSubscriptions(): void {
    this.authService.orgData$.subscribe(res => {
      this.orgData = res;
      this.isPrefSettingsAdmin =
        res?.user?.roles.includes('District Settings Admin') || false;
    });
    this.authService.userRoles$
      .pipe(takeUntil(this.headerDestroy$))
      .subscribe((roles: string[]) => {
        this.isPrefSettingsAdmin = roles.includes('District Settings Admin');
        this.isViewTypeAllowed =
          roles.includes(UserRole.LocalSystemAdmin) ||
          roles.includes(UserRole.DistricSettingsAdmin);
        const notifitcationRoles = [
          UserRole.DistricSettingsAdmin,
          UserRole.LocalSystemAdmin,
          UserRole.FidelityChecker,
          UserRole.CMSBrowser,
          UserRole.CMSDataEntry,
          UserRole.CMSManager,
          UserRole.CMSOwner,
        ];
        this.showNotificationSettings = notifitcationRoles.some(role =>
          roles.includes(role)
        );
      });
    this.authService.userData$
      .pipe(takeUntil(this.headerDestroy$))
      .subscribe(user => {
        if (user && !this.isPrefSettingsAdmin) {
          this.isPrefSettingsAdmin = user.isSuperAdmin;
        }
      });
  }

  private setupResizeListener(): void {
    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize.bind(this));
  }

  private checkScreenSize(): void {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth <= 768;
    this.layoutService.setMobileView(this.isMobile);
    if (!wasMobile && this.isMobile) {
      this.layoutService.setSidebarState(true);
    }
  }

  private checkHamburgerVisibility(): void {
    this.isHamburgerVisible = this.HAMBURGER_VISIBLE_ROUTES.some(route =>
      this.router.url.includes(route)
    );
    this.cdr.detectChanges();
  }

  private updateShowOrganizationSearch(): void {
    const raw = this.router.url.split(/[?#]/)[0];
    const normalized =
      raw.endsWith('/') && raw.length > 1 ? raw.slice(0, -1) : raw;
    this.isManageSettignRoute =
      this.MANAGE_SETTINGS_ROUTES.includes(normalized);
    this.showOrganizationSearch = normalized === '/dashboard';
  }

  onDateRangeChange(dates: Date[]): void {
    this.dateRangeError = '';

    if (dates?.length === 2) {
      const [startDate, endDate] = dates;

      if (this.isDateRangeValid(startDate, endDate)) {
        this.dateRange = dates;
        this.activityDashboardService.updateDateRange(this.dateRange);
      } else {
        this.dateRangeError = `Date range cannot exceed ${this.MAX_DAYS} days.`;
        this.dateRange = [];
      }
    } else {
      this.dateRange = dates;
      if (!dates || dates.length === 0) {
        this.activityDashboardService.updateDateRange(this.dateRange);
      }
    }
  }

  private isDateRangeValid(startDate: Date, endDate: Date): boolean {
    if (!startDate || !endDate) return true;

    const timeDifference = endDate.getTime() - startDate.getTime();
    const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));

    return daysDifference <= this.MAX_DAYS;
  }

  disabledDate = (current: Date): boolean => {
    if (!this.dateRange || this.dateRange.length === 0) {
      return false;
    }

    if (this.dateRange.length === 1) {
      const selectedDate = this.dateRange[0];
      const timeDifference = Math.abs(
        current.getTime() - selectedDate.getTime()
      );
      const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));

      return daysDifference > this.MAX_DAYS;
    }

    return false;
  };

  orgChange(value: number | null) {
    this.selectedOrganizationId = value;
    this.notificationService.setSelectedOrgId(value);
    this.orgSelection.setOrgId(value);
  }
}
